import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useContext,
  type HTMLAttributes,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from './cn';

const TableContext = /* @__PURE__ */ createContext({ stickyHeader: false });

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  /** Keep header cells visible while the container scrolls. */
  stickyHeader?: boolean;
  /**
   * Classes for the scrolling wrapper. With `stickyHeader`, give it a height
   * limit (`max-h-[60vh]`) so the header sticks within it.
   */
  containerClassName?: string;
}

/**
 * Data table in a horizontally scrolling wrapper. Separate borders keep
 * cell borders intact under a sticky header.
 */
export const Table = /* @__PURE__ */ forwardRef<HTMLTableElement, TableProps>(
  function Table(
    { stickyHeader = false, containerClassName, className, ...props },
    ref
  ) {
    return (
      <TableContext.Provider value={{ stickyHeader }}>
        <div
          className={cn('relative w-full overflow-auto', containerClassName)}
        >
          <table
            ref={ref}
            className={cn(
              'w-full border-separate border-spacing-0 text-sm',
              className
            )}
            {...props}
          />
        </div>
      </TableContext.Provider>
    );
  }
);

export const THead = /* @__PURE__ */ forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function THead({ className, ...props }, ref) {
  return <thead ref={ref} className={className} {...props} />;
});

export const TBody = /* @__PURE__ */ forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TBody({ className, ...props }, ref) {
  return (
    <tbody
      ref={ref}
      className={cn('[&>tr:last-child>td]:border-b-0', className)}
      {...props}
    />
  );
});

export interface TrProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Selected row: left accent bar and a muted fill. Pair with `aria-selected`. */
  selected?: boolean;
  /** Hover feedback for clickable rows. */
  interactive?: boolean;
}

export const Tr = /* @__PURE__ */ forwardRef<HTMLTableRowElement, TrProps>(
  function Tr(
    { selected = false, interactive = false, className, ...props },
    ref
  ) {
    return (
      <tr
        ref={ref}
        data-selected={selected ? 'true' : undefined}
        className={cn(
          'transition-colors duration-100',
          interactive && 'cursor-pointer hover:bg-surface-muted/60',
          selected &&
            'bg-surface-muted hover:bg-surface-muted [&>td:first-child]:shadow-[inset_3px_0_0_rgb(var(--color-accent))]',
          className
        )}
        {...props}
      />
    );
  }
);

export type SortDirection = 'asc' | 'desc';

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Right-align for numeric columns. */
  numeric?: boolean;
  /** Current sort of this column; `false`/undefined when unsorted. */
  sortDirection?: SortDirection | false;
  /** Makes the header a sort button. */
  onSort?: () => void;
}

export const Th = /* @__PURE__ */ forwardRef<HTMLTableCellElement, ThProps>(
  function Th(
    {
      numeric = false,
      sortDirection,
      onSort,
      className,
      children,
      scope = 'col',
      ...props
    },
    ref
  ) {
    const { stickyHeader } = useContext(TableContext);
    const SortIcon =
      sortDirection === 'asc'
        ? ArrowUp
        : sortDirection === 'desc'
          ? ArrowDown
          : ChevronsUpDown;
    return (
      <th
        ref={ref}
        scope={scope}
        aria-sort={
          sortDirection === 'asc'
            ? 'ascending'
            : sortDirection === 'desc'
              ? 'descending'
              : undefined
        }
        className={cn(
          'h-9 whitespace-nowrap border-b border-border bg-surface-sunken px-3 text-left align-middle text-[11px] font-medium uppercase tracking-wide text-fg-muted',
          numeric && 'text-right',
          stickyHeader && 'sticky top-0 z-10',
          className
        )}
        {...props}
      >
        {onSort ? (
          <button
            type="button"
            onClick={onSort}
            className={cn(
              'group -mx-1.5 inline-flex items-center gap-1 rounded px-1.5 py-1 uppercase tracking-wide transition-colors hover:text-fg',
              numeric && 'flex-row-reverse',
              sortDirection && 'text-fg'
            )}
          >
            {children}
            <SortIcon
              aria-hidden="true"
              className={cn(
                'size-3.5 shrink-0',
                !sortDirection && 'opacity-40 group-hover:opacity-100'
              )}
            />
          </button>
        ) : (
          children
        )}
      </th>
    );
  }
);

export interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Right-aligned monospace digits for coordinates and residuals. */
  numeric?: boolean;
  /** Monospace without changing alignment (identifiers). */
  mono?: boolean;
}

export const Td = /* @__PURE__ */ forwardRef<HTMLTableCellElement, TdProps>(
  function Td({ numeric = false, mono = false, className, ...props }, ref) {
    return (
      <td
        ref={ref}
        className={cn(
          'h-10 border-b border-border px-3 align-middle text-fg',
          (numeric || mono) && 'font-mono tabular-nums',
          numeric && 'text-right',
          className
        )}
        {...props}
      />
    );
  }
);
